import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getOppOrderbookExchanges,
  getOppShort1Long2MidDeviation,
  type OppShort1Long2MidDeviationItem,
  type OppShort1Long2MidDeviationResponse,
  type OppShort1Long2MidDeviationSortBy,
} from '@/services/oppDetectorApi';
import { hasCategory, renderCategoryLabel, categoryBadgeClass } from '@/lib/instrumentCategory';
import '../OppTop/OppTop.css';

const DEFAULT_LIMIT = 100;
const DEFAULT_WINDOW_SECS = 3600;
const DEFAULT_INTERVAL = 300;

const WINDOW_OPTIONS = [
  { value: 300, label: '5 分钟' },
  { value: 600, label: '10 分钟' },
  { value: 1800, label: '30 分钟' },
  { value: 3600, label: '1 小时' },
  { value: 14_400, label: '4 小时' },
  { value: 28_800, label: '8 小时' },
  { value: 86_400, label: '24 小时' },
  { value: 172_800, label: '48 小时' },
] as const;

const INTERVAL_OPTIONS = [
  { value: 30, label: '30 秒' },
  { value: 60, label: '1 分钟' },
  { value: 300, label: '5 分钟' },
  { value: 900, label: '15 分钟' },
] as const;

const SORT_OPTIONS: Array<{ value: OppShort1Long2MidDeviationSortBy; label: string }> = [
  { value: 'max_abs_deviation', label: '最大绝对偏离' },
  { value: 'exceed_0_002_ratio', label: '>0.2% 偏离占比' },
  { value: 'exceed_0_005_ratio', label: '>0.5% 偏离占比' },
  { value: 'exceed_0_01_ratio', label: '>1% 偏离占比' },
];

function formatRatio(v: number): string {
  if (!Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(2)}%`;
}

function formatDeviation(v: number): string {
  if (!Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(4)}%`;
}

function formatMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

function toUiExchange(exchange: string): string {
  const normalized = exchange.trim().toLowerCase();
  if (normalized === 'okx') return 'OKX';
  if (normalized === 'bybit') return 'Bybit';
  if (normalized === 'binance') return 'Binance';
  if (normalized === 'bitget') return 'Bitget';
  if (normalized === 'gateio') return 'Gateio';
  if (normalized === 'hyperliquid') return 'Hyperliquid';
  if (normalized === 'aster') return 'Aster';
  if (normalized === 'edgex') return 'EdgeX';
  if (normalized === 'lighter') return 'Lighter';
  if (normalized === 'paradex') return 'Paradex';
  return exchange;
}

function emptyPage(exchange1: string, exchange2: string): OppShort1Long2MidDeviationResponse {
  return {
    exchange1,
    exchange2,
    start_ms: 0,
    end_ms: 0,
    interval: DEFAULT_INTERVAL,
    sort_by: 'max_abs_deviation',
    total: 0,
    offset: 0,
    limit: DEFAULT_LIMIT,
    items: [],
  };
}

const FETCH_ALL_PAGE_SIZE = 500;

function sortScoreFor(
  item: OppShort1Long2MidDeviationItem,
  sortBy: OppShort1Long2MidDeviationSortBy,
): number {
  switch (sortBy) {
    case 'exceed_0_002_ratio':
      return item.stats.exceed_0_002_ratio;
    case 'exceed_0_005_ratio':
      return item.stats.exceed_0_005_ratio;
    case 'exceed_0_01_ratio':
      return item.stats.exceed_0_01_ratio;
    default:
      return item.stats.max_abs_deviation;
  }
}

async function fetchAllMidDeviationItems(params: {
  exchange1: string;
  exchange2: string;
  window_secs: number;
  interval: number;
}): Promise<OppShort1Long2MidDeviationResponse> {
  let offset = 0;
  let total = Number.POSITIVE_INFINITY;
  const items: OppShort1Long2MidDeviationItem[] = [];
  let meta: OppShort1Long2MidDeviationResponse | null = null;

  while (offset < total) {
    const page = await getOppShort1Long2MidDeviation({
      ...params,
      sort_by: 'max_abs_deviation',
      limit: FETCH_ALL_PAGE_SIZE,
      offset,
    });
    if (!meta) meta = page;
    items.push(...page.items);
    total = page.total;
    offset += page.items.length;
    if (page.items.length === 0) break;
  }

  if (!meta) {
    return emptyPage(params.exchange1, params.exchange2);
  }

  return {
    ...meta,
    total: items.length,
    items,
    offset: 0,
    limit: items.length,
  };
}

export function OppShort1Long2MidDeviation() {
  const [exchanges, setExchanges] = useState<string[]>([]);
  const [exchange1, setExchange1] = useState('');
  const [exchange2, setExchange2] = useState('');
  const [windowSecs, setWindowSecs] = useState(DEFAULT_WINDOW_SECS);
  const [interval, setInterval] = useState(DEFAULT_INTERVAL);
  const [sortBy, setSortBy] = useState<OppShort1Long2MidDeviationSortBy>('max_abs_deviation');
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offset, setOffset] = useState(0);
  const [allItems, setAllItems] = useState<OppShort1Long2MidDeviationItem[]>([]);
  const [queryMeta, setQueryMeta] = useState<Omit<OppShort1Long2MidDeviationResponse, 'items' | 'total' | 'offset' | 'limit' | 'sort_by'>>(() => ({
    exchange1: '',
    exchange2: '',
    start_ms: 0,
    end_ms: 0,
    interval: DEFAULT_INTERVAL,
  }));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastOkAt, setLastOkAt] = useState<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    void getOppOrderbookExchanges()
      .then((list) => {
        if (!mounted.current) return;
        setExchanges(list);
        if (list.length >= 2) {
          setExchange1((prev) => prev || list[0]);
          setExchange2((prev) => prev || list[1]);
        }
      })
      .catch((e) => {
        if (!mounted.current) return;
        setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!exchange1 || !exchange2 || exchange1 === exchange2) {
      setAllItems([]);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchAllMidDeviationItems({
        exchange1,
        exchange2,
        window_secs: windowSecs,
        interval,
      });
      if (!mounted.current) return;
      setAllItems(data.items);
      setQueryMeta({
        exchange1: data.exchange1,
        exchange2: data.exchange2,
        start_ms: data.start_ms,
        end_ms: data.end_ms,
        interval: data.interval,
      });
      setError(null);
      setLastOkAt(Date.now());
    } catch (e) {
      if (!mounted.current) return;
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [exchange1, exchange2, interval, windowSecs]);

  const resetOffset = useCallback(() => setOffset(0), []);

  const page = useMemo((): OppShort1Long2MidDeviationResponse => {
    const sorted = [...allItems]
      .map((item) => ({
        ...item,
        sort_score: sortScoreFor(item, sortBy),
      }))
      .sort((a, b) => b.sort_score - a.sort_score);

    const total = sorted.length;
    const safeOffset = Math.min(offset, total);
    const items = sorted.slice(safeOffset, safeOffset + limit);

    return {
      ...queryMeta,
      sort_by: sortBy,
      total,
      offset: safeOffset,
      limit,
      items,
    };
  }, [allItems, limit, offset, queryMeta, sortBy]);

  const rows = page.items;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(page.total / Math.max(page.limit, 1))),
    [page.limit, page.total],
  );
  const currentPage = useMemo(
    () => Math.floor(page.offset / Math.max(page.limit, 1)) + 1,
    [page.limit, page.offset],
  );

  const jumpToOppSpreadLive = useCallback((item: OppShort1Long2MidDeviationItem) => {
    const params = new URLSearchParams({
      symbol: item.symbol,
      exchange1: toUiExchange(item.exchange1),
      exchange2: toUiExchange(item.exchange2),
      autoQuery: '1',
    });
    const url = `${window.location.origin}/trading?${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const canQuery = Boolean(exchange1 && exchange2 && exchange1 !== exchange2);

  return (
    <div className="opp-top-page">
      <header className="opp-top-header">
        <h2>Short1Long2 Mid 偏离榜</h2>
        <p className="opp-top-desc">
          自动扫描两所全部公共 symbol，基于 <code>/api/opp-detector/spread/detailed</code> 计算
          short1_long2 mid spread 相对 rolling median 的偏离统计，按所选指标倒序排列。
        </p>
      </header>

      <div className="opp-top-toolbar">
        <button type="button" disabled={loading || !canQuery} onClick={() => void load()}>
          {loading ? '查询中...' : '查询'}
        </button>
        <label className="opp-top-filter-field">
          <span>交易所 1</span>
          <select
            value={exchange1}
            onChange={(e) => {
              setExchange1(e.target.value);
              resetOffset();
            }}
          >
            <option value="">请选择</option>
            {exchanges.map((exchange) => (
              <option key={exchange} value={exchange}>
                {exchange}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>交易所 2</span>
          <select
            value={exchange2}
            onChange={(e) => {
              setExchange2(e.target.value);
              resetOffset();
            }}
          >
            <option value="">请选择</option>
            {exchanges.map((exchange) => (
              <option key={exchange} value={exchange}>
                {exchange}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>时间窗口</span>
          <select
            value={windowSecs}
            onChange={(e) => {
              setWindowSecs(Number(e.target.value) || DEFAULT_WINDOW_SECS);
              resetOffset();
            }}
          >
            {WINDOW_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>Interval</span>
          <select
            value={interval}
            onChange={(e) => {
              setInterval(Number(e.target.value) || DEFAULT_INTERVAL);
              resetOffset();
            }}
          >
            {INTERVAL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>排序指标</span>
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value as OppShort1Long2MidDeviationSortBy);
              resetOffset();
            }}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="opp-top-filter-field">
          <span>每页条数</span>
          <input
            type="number"
            min={1}
            step={1}
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value);
              setLimit(Number.isFinite(next) && next > 0 ? Math.floor(next) : DEFAULT_LIMIT);
              resetOffset();
            }}
          />
        </label>
        <button
          type="button"
          disabled={loading || offset <= 0}
          onClick={() => setOffset((prev) => Math.max(0, prev - limit))}
        >
          上一页
        </button>
        <button
          type="button"
          disabled={loading || offset + limit >= page.total}
          onClick={() => setOffset((prev) => prev + limit)}
        >
          下一页
        </button>
        <p className="opp-top-meta">
          第 {currentPage} / {totalPages} 页，共 {page.total} 条
        </p>
        {page.start_ms > 0 && (
          <p className="opp-top-meta">
            查询范围：{formatMs(page.start_ms)} — {formatMs(page.end_ms)}
          </p>
        )}
        {lastOkAt != null && (
          <p className="opp-top-meta">上次成功：{new Date(lastOkAt).toLocaleString('zh-CN', { hour12: false })}</p>
        )}
      </div>

      {!canQuery && (
        <div className="opp-top-error">请选择两个不同的交易所后再查询。</div>
      )}

      {error && <div className="opp-top-error">{error}</div>}

      <div className="opp-top-table-wrap">
        {rows.length === 0 && !loading && !error && canQuery && allItems.length === 0 ? (
          <div className="opp-top-empty">点击「查询」获取数据（首次扫描全部公共 symbol 可能较慢）</div>
        ) : rows.length === 0 && !loading ? (
          <div className="opp-top-empty">暂无数据</div>
        ) : (
          <table className="opp-top-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Symbol</th>
                <th>交易所对</th>
                <th>inst_id 1</th>
                <th>inst_id 2</th>
                <th className="num">排序分</th>
                <th className="num">最大偏离</th>
                <th className="num">&gt;0.2%</th>
                <th className="num">&gt;0.5%</th>
                <th className="num">&gt;1%</th>
                <th className="num">样本点</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={`${r.symbol}-${r.inst_id1}-${r.inst_id2}-${i}`}
                  className="opp-top-row-clickable"
                  onClick={() => jumpToOppSpreadLive(r)}
                  title="点击跳转到 Opp Spread/K 线并自动查询"
                >
                  <td>{page.offset + i + 1}</td>
                  <td>
                    <span className="opp-top-symbol-cell">
                      <span>{r.symbol}</span>
                      {hasCategory(r.category) && (
                        <span className={categoryBadgeClass(r.category)}>{renderCategoryLabel(r.category)}</span>
                      )}
                    </span>
                  </td>
                  <td>
                    {r.exchange1} ↔ {r.exchange2}
                  </td>
                  <td>
                    <span title={r.inst_id1}>{r.inst_id1}</span>
                  </td>
                  <td>
                    <span title={r.inst_id2}>{r.inst_id2}</span>
                  </td>
                  <td className="num">
                    {sortBy === 'max_abs_deviation'
                      ? formatDeviation(r.sort_score)
                      : formatRatio(r.sort_score)}
                  </td>
                  <td className="num">{formatDeviation(r.stats.max_abs_deviation)}</td>
                  <td className="num">{formatRatio(r.stats.exceed_0_002_ratio)}</td>
                  <td className="num">{formatRatio(r.stats.exceed_0_005_ratio)}</td>
                  <td className="num">{formatRatio(r.stats.exceed_0_01_ratio)}</td>
                  <td className="num">{r.stats.total_points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
